/**
 * newDirectMessage.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/
import bluebird from "bluebird";
import { ObjectId } from "bson";
import moment from "moment-timezone";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import { Autocomplete, Checkbox, FormControlLabel, IconButton, TextField } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { ChatInput } from "../chat/chatInput";
import { GroupHistoryContainer } from "../chat/groupHistoryContainer";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Device, Membership } from "../models/auth";
import { RatchetTree } from "../models/crypto";
import { Group, GroupMembership, GroupState, GroupSubmissionType, GroupType, PROTOCOL_VERSION } from "../models/group";
import { ContentMessage } from "../models/message";

import { groupServiceFactory } from "../services/group.service";
import { authServiceFactory } from "../services/auth.service";
import { bufferToBase64 } from "../services/encoding.service";

interface DeviceSelection {
  hasPubKey: boolean,
  selected: boolean,
  disabled: boolean,
  device: Device
}

type ResolveGroupResult = [string, string, Array<Membership>, {[userID: string]: DeviceSelection[]}];

export function NewDirectMessageComponent(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  const [curGroupID, setCurGroupID] = React.useState<string>("");
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [selectedMembers, setSelectedMembers] = React.useState<Array<Membership>>([]);
  const [deviceMap, setDeviceMap] = React.useState<{[userID: string]: DeviceSelection[]}>({});
  const [classes, setClasses] = React.useState<string>("");
  const [userHash, setUserHash] = React.useState<string>("");
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [dmDraft, setDMDraft] = React.useState<string>("");
  const navigate = useNavigate();
  
  // mitigates a race condition between querying for an existing group and
  // completing the message to send to the group
  const [groupIDPromise, setGroupIDPromise] = React.useState<Promise<ResolveGroupResult>>(
    bluebird.resolve([curGroupID, userHash, selectedMembers, deviceMap]));
  
  React.useEffect(function () {
    if (timer) { clearTimeout(timer); }
    setTimer(setTimeout(function () {
      const authService = authServiceFactory();
      authService.getAccountMembers(inputValue, 100).then(mems => {
        mems.sort((a, b): number => {
          const res = a.lastname.localeCompare(b.lastname);
          if  (res != 0) { return res; }
          return a.firstname.localeCompare(b.firstname);
        });
        setMembers(mems);
      }, err => {
        console.error("Error fetching members:", err);
        setErrMsg((err as Error).message);
      });
    }, 500));
  }, [inputValue]);

  async function finishDM(msg: ContentMessage) {
    try {
      
      // the groupIDPromise function updates the state. If it does that after this
      // function is called, any state variables we've captured in this context
      // will be stale. So we need all the variables to be provided by the promise.
      const [gID, hash, selectedMemberships, deviceSelectionMap] = await groupIDPromise;

      const groupID = gID || new ObjectId().toHexString();
      let rtree: RatchetTree | null = null;
      let myGroupMembership: GroupMembership | null | undefined = null;

      for (const member of selectedMemberships) {
        if (!Object.prototype.hasOwnProperty.call(deviceSelectionMap, member.userID)) {
          setErrMsg("Devices not found for " +
            (member ? member.firstname + " " + member.lastname : "<Unknown>"));
          return;
        }
        const devices = deviceSelectionMap[member.userID];
        const selected = devices.find(d => d.selected);
        if (!selected) {
          setErrMsg("Please, select at least 1 device for " +
            (member ? member.firstname + " " + member.lastname : "Not Found"));
          return;
        }
      }

      // Step 1: Make the group if we need to
      if (!authContext.membership) { return; }

      const groupService = groupServiceFactory();

      if (curGroupID) {
        myGroupMembership = groupNavContext.groupMembershipMap[groupID];
        if (!myGroupMembership) {
          throw new Error("Couldn't find existing Direct Message to user(s)");
        }
        rtree = await groupNavContext.setCurrentGroup(myGroupMembership);

        if (!rtree) {
          [rtree, myGroupMembership] = await groupService.join(
            authContext.membership.accountID, authContext.membership.userID,
            groupID);
        }

      } else {
        [rtree, myGroupMembership] = await groupNavContext.finishAddGroup({
          _id: groupID,
          accountID: authContext.membership.accountID,
          groupname: "",
          grouptype: GroupType.dm,
          submissiontype: GroupSubmissionType.normal,
          groupstate: GroupState.active,
          desc: "",
          epoch: 0,
          msgCounter: 0,
          archiving: false,
          protoVersion: PROTOCOL_VERSION,
          userHash: hash
        });
      }

      if (!rtree || !myGroupMembership) { return; }

      // Find the devices we need to add to the group
      const newDevices: Array<Device> = [];
      const acctMemberMap: {[id: string]: Membership} = {};
      for (const member of selectedMemberships) {
        acctMemberMap[member.userID] = member;
        if (!Object.prototype.hasOwnProperty.call(deviceSelectionMap, member.userID)) {
          console.log("No devices for", member.userID);
          throw new Error(`Missing devices for member: ${member.firstname} ${member.lastname}`);
        }
        for (const devSel of deviceSelectionMap[member.userID]) {
          if (devSel.selected && !devSel.disabled &&
              (!rtree || rtree.indexOfDevice(devSel.device._id) < 0)) {
            newDevices.push(devSel.device);
          }
        }
      }

      // Add devices to group
      if (newDevices.length > 0) {
        const groupMembers = await groupService.addGroupMembers(authContext.membership.accountID,
          groupID, newDevices);
        myGroupMembership.groupname = groupService.makeDirectMessageName(
          myGroupMembership, groupMembers, acctMemberMap);
      }

      myGroupMembership.haveRatchetTree = true;
      myGroupMembership.groupstate = GroupState.active;
      groupNavContext.setCurrentGroup(myGroupMembership);

      // Finally, send the message to the group
      msg.groupID = myGroupMembership.groupID;
      await groupService.sendMessage(authContext.membership.accountID,
        myGroupMembership.groupID, msg);

      // Redirect to new dm
      navigate(`/group/${groupID}`);
    } catch (err) {
      console.error("Error sending DM message:", err);
      setErrMsg((err as Error).message);
    }
  }

  async function doRemoveMessage(msgID: string, frameID: string | undefined, threadID: string | undefined) {
    try {
      if (!groupNavContext.currentGroup || !authContext.membership) {
        setErrMsg("Please, select a group");
        return;
      }
      const groupService = groupServiceFactory();
      if (threadID) {
        await groupService.removeReply(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID,
          threadID,
          msgID, frameID);
      } else {
        await groupService.removeMessage(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID,
          msgID, frameID);
      }
    } catch (err) {
      console.error("Error removing message:", err);
      setErrMsg((err as Error).message);
      // there's nothing above to catch, so don't throw
    }
  }

  async function doRemoveMessageLocally(msgID: string, _frameID: string | undefined, threadID: string | undefined) {
    if (!groupNavContext.currentGroup || !authContext.membership) { return; }
    try {
      if (threadID) {
        await groupServiceFactory().removeReplyLocally(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID, threadID, msgID);
      } else {
        await groupServiceFactory().removeMessageLocally(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID, msgID);
      }
    } catch (err) {
      console.error("Error removing message locally:", err);
      setErrMsg((err as Error).message);
    }
  }

  async function selectedOption (_event: React.SyntheticEvent, values: readonly Membership[] | null) {
    if (!values) { return; }
    try {
      const prom = resolveGroupAndDevices(values);
      setGroupIDPromise(prom);
      await prom;
    } catch (err) {
      console.error("Error fetching devices:", err);
      setErrMsg((err as Error).message);
    }
  }
  async function resolveGroupAndDevices(values: readonly Membership[] | null): Promise<ResolveGroupResult> {
    if (!values) { return [curGroupID, userHash, selectedMembers, deviceMap]; }
  
    const newMembers = Array.from(values);
    setSelectedMembers(newMembers);

    const {group, hash} = await findDM(newMembers);
    const newUsers = values?.filter(mem => !deviceMap[mem.userID]);
    const authService = authServiceFactory();
  
    if (hash === userHash && newUsers.length <= 0) { return [curGroupID, hash, selectedMembers, deviceMap]; }

    // If same hash, same groupID
    const groupID = hash === userHash ? curGroupID : group?._id || "";

    const newDeviceMap: {[id: string]: DeviceSelection[]} = Object.assign(
      {}, ...await bluebird.map(newUsers, mem => {
        return authService.getMemberDevices(mem.userID).then(devices => {
          return {
            [mem.userID]: devices.map(d => {return {
              device: d,
              hasPubKey: !!d.publicKey,
              disabled: !d.publicKey,
              selected: false
            } as DeviceSelection }).sort(function (a, b): number {
              if (a.device.modified?.isBefore(b.device.modified)) {
                return 1;
              } else if (b.device.modified?.isBefore(a.device.modified)) {
                return -1;
              }
              return 0;
            })
          };
        });
      })
    );

    const devMap = {
      ...deviceMap,
      ...newDeviceMap
    };

    if (group) {
      const devSet = new Set<string>();
      const myDeviceID = groupServiceFactory().deviceID;
      if (myDeviceID) { devSet.add(myDeviceID); }
      if (group.treenodes) {
        for (const node of group.treenodes) {
          if (node.deviceID) { devSet.add(node.deviceID); }
        }
      }
      for (const userID in devMap) {
        if (!Object.prototype.hasOwnProperty.call(devMap, userID)) { continue; }
        const devices = devMap[userID];
        for (const devSel of devices) {
          devSel.selected = devSet.has(devSel.device._id);
        }
      }
    } else {
      const sevenDaysAgo = moment().add(-7, "days");
      for (const userID in devMap) {
        if (!Object.prototype.hasOwnProperty.call(devMap, userID)) { continue; }
        const devices = devMap[userID];
        for (const devSel of devices) {
          const modDate = devSel.device.modified || devSel.device.created;
          devSel.selected = modDate && modDate.isAfter(sevenDaysAgo) || false;
        }
      }
    }

    setUserHash(hash);
    setDeviceMap(devMap);
    setCurGroupID(groupID);

    return [groupID, hash, newMembers, devMap];
  }

  function changeSelection (userID: string, index: number) {
    const newDevices = [...deviceMap[userID]];
    const devSel = newDevices[index];
    devSel.selected = !devSel.selected;
    setDeviceMap({
      ...deviceMap,
      [userID]: newDevices
    });
  }

  function focusedMemberSelect() {
    setClasses(" show-members");
  }

  async function computeUserHash(membership: Membership, members: Membership[]): Promise<string> {
    const allUserIDs = members.map(mem => mem.userID);
    if (allUserIDs.indexOf(membership.userID) < 0) {
      allUserIDs.push(membership.userID);
    }
    allUserIDs.sort((a, b) => a.localeCompare(b));
    const userIDStr = allUserIDs.reduce((val: string, id) => val += id, "");
    return bufferToBase64(await crypto.subtle.digest(
      "SHA-256", new TextEncoder().encode(userIDStr)));
  }
  async function findDM( members: Membership[]): Promise<{group: Group | null, hash: string}> {
    if (!authContext.membership) { return {group: null, hash: ""}; }
    const digest = await computeUserHash(authContext.membership, members);
    if (digest === userHash) { return {group: null, hash: digest}; }

    const group = await groupServiceFactory().findPrivateDirectMessage(
        authContext.membership.accountID, digest);
    return {group, hash: digest};
  }

  async function focusedChatInput() {
    setClasses(" show-chat-history");
  }

  function renderSelectedMember (member: Membership): React.ReactElement<any, any> {
    return <div key={member.userID}>
      <h3>{member.firstname} {member.lastname}</h3>
      {deviceMap[member.userID]?.map((devSel, index) => {
        const tz = authContext.tz();
        if (tz) {
          devSel.device.modified?.tz(tz);
        }
        return <div className="group-member-device-container" key={devSel.device._id}>
        <FormControlLabel className={devSel.hasPubKey ? "" : "missing-pub-key"} control={
          <Checkbox color="secondary"
            disableFocusRipple={true}
            disableRipple={true}
            disabled={devSel.disabled}
            checked={devSel.selected}
            onChange={() => changeSelection(member.userID, index) } />}
          label={<div>
              <h5 className="device-name">{devSel.device.displayString || devSel.device._id}</h5>
              {devSel.device.modified && <span className="desc">Last Seen: {devSel.device.modified.format("MMM D, YYYY h:mma")}</span>}
            </div>}
        />
        </div>
        })
      }
    </div>
  }

  return <div className={"create-dm-container" + classes}>
    <header className="group-header-nav attached-left-nav">
    <IconButton className="left-nav-outer-toggle" size="small"
        disableRipple disableTouchRipple
        onClick={groupNavContext.toggleGroupList}>
      <MenuIcon />
    </IconButton>
      <h3>New Direct Message</h3>
    </header>

    <div className="add-members-container">
      <div className="member-search-container attached-left-nav">
        <Autocomplete id="account-member-search"
          multiple
          fullWidth
          options={members}
          getOptionLabel={(member: Membership) => `${member.firstname} ${member.lastname}`}
          isOptionEqualToValue={(member, value) => member.accountID === value.accountID && member.userID === value.userID}
          onChange={selectedOption}
          onFocus={focusedMemberSelect}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) =>
            <TextField variant="outlined" color="secondary" {...params} placeholder="To:" />} />
      </div>

      <div className="add-member-list">
        {selectedMembers.map(renderSelectedMember)}
      </div>
    </div>


    <div className="group-messages-container">
      <GroupHistoryContainer
          authContext={authContext}
          scrollingElem={groupNavContext.mainScrollingElement()}
          loadingGroup={false}
          groupID={curGroupID}
          myUserID={authContext.membership?.userID || ""}
          doRemoveMessage={doRemoveMessage}
          doRemoveMessageLocally={doRemoveMessageLocally}
          onError={msg => setErrMsg(msg)} />
    </div>

    <div className="attached-left-nav group-input-container">
      <ChatInput
        groupID={curGroupID}
        threadID=""
        myUserID={authContext.membership?.userID}
        sendOnReturnKey={authContext.membership?.sendOnReturn || false}
        getDraft={() => Promise.resolve(dmDraft)}
        saveDraft={setDMDraft}
        onSend={finishDM}
        onFocus={focusedChatInput}
        getMatchingMembers={groupNavContext.getMatchingMembers}
        onError={msg => setErrMsg(msg)} />
    </div>
    <ErrorSnackBar errMsg={errMsg} />
  </div>;
}

